import React, { useEffect, useMemo, useState } from "react";
import SubCard from "../../ui-component/cards/SubCard";
import {
  Button,
  Checkbox,
  Grid,
  MenuItem,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ArrowDropUpIcon from "@material-ui/icons/ArrowDropUp";
import FilterListIcon from "@material-ui/icons/FilterList";
import UiChip from "../../ui-component/extended/Chip";
import { LoadingButton } from "@mui/lab";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import { Link } from "react-router-dom";
import moment from "moment";

const STAGE_OPTIONS = [0, 1];
const RESPONSE_OPTIONS = ["not_sent", "pending", "confirmed", "no"];

const EMPTY_FILTERS = {
  orderId: "",
  phone: "",
  stage: [],
  response: [],
  sentFrom: "",
  sentTo: "",
  respondedFrom: "",
  respondedTo: "",
};

// One popover per header: checkbox list for "select"-type columns (Excel-style
// autofilter), a text box for "search"-type columns, a from/to range for "date"
// columns. Sortable columns get an Asc/Desc toggle at the top of the same popover.
function FilterableHeader({ label, type, options, value, onChange, sortable, sortDir, onSort }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isActive =
    type === "select"
      ? value.length > 0
      : type === "search"
      ? !!value
      : type === "date"
      ? !!(value.from || value.to)
      : false;

  return (
    <TableCell>
      <div style={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }} onClick={(e) => setAnchorEl(e.currentTarget)}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        {sortable && sortDir === "asc" && <ArrowDropUpIcon fontSize="small" />}
        {sortable && sortDir === "desc" && <ArrowDropDownIcon fontSize="small" />}
        {(type === "select" || type === "search" || type === "date") && (
          <FilterListIcon fontSize="small" style={{ marginLeft: 4, color: isActive ? "#1976d2" : "#9e9e9e" }} />
        )}
      </div>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <div style={{ padding: 12, minWidth: 220 }}>
          {sortable && (
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <Button size="small" variant={sortDir === "asc" ? "contained" : "outlined"} onClick={() => onSort("asc")}>
                Sort Asc
              </Button>
              <Button size="small" variant={sortDir === "desc" ? "contained" : "outlined"} onClick={() => onSort("desc")}>
                Sort Desc
              </Button>
            </div>
          )}

          {type === "select" && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <Button size="small" onClick={() => onChange([])}>
                  All
                </Button>
                <Button size="small" onClick={() => onChange(options.map((o) => o.value))}>
                  None
                </Button>
              </div>
              {options.map((opt) => (
                <div key={String(opt.value)} style={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    size="small"
                    checked={value.length === 0 || value.includes(opt.value)}
                    onChange={() => {
                      const active = value.length === 0 ? options.map((o) => o.value) : value;
                      const next = active.includes(opt.value) ? active.filter((v) => v !== opt.value) : [...active, opt.value];
                      onChange(next.length === options.length ? [] : next);
                    }}
                  />
                  <Typography variant="body2">{opt.label}</Typography>
                </div>
              ))}
            </>
          )}

          {type === "search" && (
            <TextField
              size="small"
              fullWidth
              autoFocus
              placeholder={`Search ${label}`}
              value={value}
              onChange={(e) => onChange(e.target.value)}
            />
          )}

          {type === "date" && (
            <>
              <TextField
                size="small"
                fullWidth
                type="date"
                label="From"
                InputLabelProps={{ shrink: true }}
                value={value.from}
                onChange={(e) => onChange({ ...value, from: e.target.value })}
                style={{ marginBottom: 8 }}
              />
              <TextField
                size="small"
                fullWidth
                type="date"
                label="To"
                InputLabelProps={{ shrink: true }}
                value={value.to}
                onChange={(e) => onChange({ ...value, to: e.target.value })}
              />
            </>
          )}
        </div>
      </Popover>
    </TableCell>
  );
}

function OrderConfirmationPanel() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ field: "orderConfirmationMsgSentAt", dir: "desc" });

  const loadData = (currentFilter = filter) => {
    setLoading(true);
    setSelectedIds([]);
    axios
      .post(process.env.REACT_APP_PATH_BULK + "/products/getOrderConfirmationPanel", {
        filter: currentFilter,
      })
      .then((res) => {
        if (res.data.success) {
          setOrders(res.data.data || []);
        } else {
          alert(res.data?.message || "Something went wrong");
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilter(value);
    loadData(value);
  };

  const responseChip = (response) => {
    if (response === "confirmed") return <UiChip label="Confirmed" size="small" chipcolor="success" />;
    if (response === "no") return <UiChip label="No" size="small" chipcolor="orange" />;
    if (response === "not_sent") return <UiChip label="Not Sent" size="small" chipcolor="secondary" />;
    return <UiChip label="Pending" size="small" />;
  };

  const setFilterField = (field, value) => setFilters((f) => ({ ...f, [field]: value }));

  const toggleSort = (field, dir) => setSort({ field, dir });

  const filteredSortedOrders = useMemo(() => {
    let rows = orders.filter((o) => {
      if (filters.orderId && !String(o.numericOrderId).includes(filters.orderId.trim())) return false;
      if (filters.phone && !String(o.phoneNumber || "").includes(filters.phone.trim())) return false;
      if (filters.stage.length && !filters.stage.includes(o.orderStageV2)) return false;
      if (filters.response.length && !filters.response.includes(o.orderConfirmationResponse)) return false;

      if (filters.sentFrom && (!o.orderConfirmationMsgSentAt || new Date(o.orderConfirmationMsgSentAt) < new Date(filters.sentFrom))) return false;
      if (filters.sentTo && (!o.orderConfirmationMsgSentAt || new Date(o.orderConfirmationMsgSentAt) > new Date(filters.sentTo + "T23:59:59")))
        return false;

      if (
        filters.respondedFrom &&
        (!o.orderConfirmationRespondedAt || new Date(o.orderConfirmationRespondedAt) < new Date(filters.respondedFrom))
      )
        return false;
      if (
        filters.respondedTo &&
        (!o.orderConfirmationRespondedAt || new Date(o.orderConfirmationRespondedAt) > new Date(filters.respondedTo + "T23:59:59"))
      )
        return false;

      return true;
    });

    if (sort.field) {
      rows = [...rows].sort((a, b) => {
        let av = a[sort.field];
        let bv = b[sort.field];
        if (sort.field === "orderConfirmationMsgSentAt" || sort.field === "orderConfirmationRespondedAt") {
          av = av ? new Date(av).getTime() : 0;
          bv = bv ? new Date(bv).getTime() : 0;
        }
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      });
    }

    return rows;
  }, [orders, filters, sort]);

  const selectableIds = filteredSortedOrders.filter((o) => o.canResend).map((o) => o.numericOrderId);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : selectableIds);
  };

  const toggleSelectOne = (numericOrderId) => {
    setSelectedIds((prev) => (prev.includes(numericOrderId) ? prev.filter((id) => id !== numericOrderId) : [...prev, numericOrderId]));
  };

  const resendSelected = () => {
    if (!selectedIds.length) return;
    setResending(true);
    axios
      .post(process.env.REACT_APP_PATH_BULK + "/products/resendOrderConfirmation", {
        orderIds: selectedIds,
      })
      .then((res) => {
        if (res.data.success) {
          const results = res.data.data || [];
          const sent = results.filter((r) => r.ok).length;
          const failed = results.filter((r) => !r.ok);
          let msg = `Resent to ${sent}/${results.length} order(s).`;
          if (failed.length) {
            msg += `\n\nFailed:\n` + failed.map((f) => `${f.numericOrderId}: ${f.message}`).join("\n");
          }
          alert(msg);
          loadData();
        } else {
          alert(res.data?.message || "Something went wrong");
        }
      })
      .catch((err) => {
        alert(err?.response?.data?.message || "Something went wrong while resending");
      })
      .finally(() => {
        setResending(false);
      });
  };

  return (
    <SubCard title="Order Confirmation Panel">
      <Grid container spacing={2} alignItems="center" style={{ marginBottom: 16 }}>
        <Grid item>
          <Select value={filter} onChange={handleFilterChange} size="small">
            <MenuItem value="all">All (Sent + Not Sent Yet)</MenuItem>
            <MenuItem value="notSent">Not Sent Yet — Eligible</MenuItem>
            <MenuItem value="no">Buyer Said No — Call Them</MenuItem>
            <MenuItem value="overdue">No Response &gt; 6 Hours</MenuItem>
          </Select>
        </Grid>
        <Grid item>
          <Button variant="outlined" onClick={() => loadData()} disabled={loading}>
            Refresh
          </Button>
        </Grid>
        <Grid item>
          <Button size="small" variant="text" onClick={() => setFilters(EMPTY_FILTERS)}>
            Clear Filters
          </Button>
        </Grid>
        <Grid item>
          <LoadingButton variant="contained" color="primary" loading={resending} disabled={selectedIds.length === 0} onClick={resendSelected}>
            Send/Resend Selected ({selectedIds.length})
          </LoadingButton>
        </Grid>
        <Grid item>
          <Typography variant="caption" color="textSecondary">
            Click a column header to sort/filter it. Only Stage 0 (unconfirmed) orders can be selected/resent.
          </Typography>
        </Grid>
      </Grid>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox size="small" checked={allSelected} onChange={toggleSelectAll} disabled={selectableIds.length === 0} />
              </TableCell>
              <FilterableHeader
                label="Order Id"
                type="search"
                value={filters.orderId}
                onChange={(v) => setFilterField("orderId", v)}
              />
              <TableCell style={{ fontWeight: 600 }}>Name</TableCell>
              <FilterableHeader label="Phone" type="search" value={filters.phone} onChange={(v) => setFilterField("phone", v)} />
              <TableCell style={{ fontWeight: 600 }}>Address</TableCell>
              <FilterableHeader
                label="Order Stage"
                type="select"
                options={STAGE_OPTIONS.map((v) => ({ value: v, label: `Stage ${v}` }))}
                value={filters.stage}
                onChange={(v) => setFilterField("stage", v)}
              />
              <FilterableHeader
                label="Message Sent At"
                type="date"
                value={{ from: filters.sentFrom, to: filters.sentTo }}
                onChange={(v) => setFilters((f) => ({ ...f, sentFrom: v.from, sentTo: v.to }))}
                sortable
                sortDir={sort.field === "orderConfirmationMsgSentAt" ? sort.dir : null}
                onSort={(dir) => toggleSort("orderConfirmationMsgSentAt", dir)}
              />
              <FilterableHeader
                label="Response"
                type="select"
                options={RESPONSE_OPTIONS.map((v) => ({
                  value: v,
                  label: v === "not_sent" ? "Not Sent" : v.charAt(0).toUpperCase() + v.slice(1),
                }))}
                value={filters.response}
                onChange={(v) => setFilterField("response", v)}
              />
              <FilterableHeader
                label="Responded At"
                type="date"
                value={{ from: filters.respondedFrom, to: filters.respondedTo }}
                onChange={(v) => setFilters((f) => ({ ...f, respondedFrom: v.from, respondedTo: v.to }))}
                sortable
                sortDir={sort.field === "orderConfirmationRespondedAt" ? sort.dir : null}
                onSort={(dir) => toggleSort("orderConfirmationRespondedAt", dir)}
              />
              <TableCell style={{ fontWeight: 600 }}>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSortedOrders.map((o) => (
              <TableRow key={o.numericOrderId} style={o.isOverdue ? { background: "#ffebee" } : {}}>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(o.numericOrderId)}
                    disabled={!o.canResend}
                    onChange={() => toggleSelectOne(o.numericOrderId)}
                  />
                </TableCell>
                <TableCell>{o.numericOrderId}</TableCell>
                <TableCell>{o.name}</TableCell>
                <TableCell>{o.phoneNumber}</TableCell>
                <TableCell>
                  {[o.address?.address1, o.address?.address2, o.address?.landmark, o.address?.postalCode].filter(Boolean).join(", ")}
                </TableCell>
                <TableCell>
                  <UiChip label={`Stage ${o.orderStageV2}`} size="small" chipcolor={o.orderStageV2 === 0 ? "primary" : "success"} />
                </TableCell>
                <TableCell>
                  {o.orderConfirmationMsgSentAt ? (
                    (o.orderConfirmationMsgSentAtLog || []).length > 1 ? (
                      <Tooltip
                        title={
                          <span>
                            {o.orderConfirmationMsgSentAtLog.map((d, i) => (
                              <div key={i}>
                                {i + 1}. {moment(d).format("DD MMM, hh:mm A")}
                              </div>
                            ))}
                          </span>
                        }
                      >
                        <span>
                          {moment(o.orderConfirmationMsgSentAt).format("DD MMM, hh:mm A")}{" "}
                          <UiChip label={`${o.orderConfirmationMsgSentAtLog.length}x`} size="small" />
                        </span>
                      </Tooltip>
                    ) : (
                      moment(o.orderConfirmationMsgSentAt).format("DD MMM, hh:mm A")
                    )
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>{responseChip(o.orderConfirmationResponse)}</TableCell>
                <TableCell>{o.orderConfirmationRespondedAt ? moment(o.orderConfirmationRespondedAt).format("DD MMM, hh:mm A") : "—"}</TableCell>
                <TableCell>
                  <Button size="small" variant="outlined" component={Link} to={`/bulk/bulkorderdetails/${o._id}`} target="_blank">
                    Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredSortedOrders.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography align="center">No orders found</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SubCard>
  );
}

export default OrderConfirmationPanel;
